import { UserFavorite } from '@engelvoelkersamericas/api';
import evAPI from '@util/api/ev';
import axios from 'axios';
import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

interface GlobalSiteHeaderContextType {
  modalSection: string;
  toSetModalSection: (section: string) => void;
  placesSearchObj: any[] | null;
  saveListing: (userId: string, listingId: string, listOfficeMlsId: string) => void;
  deleteSavedListing: (userId: string, listingId: string) => void;
  isListingFavorited: (listingId: string, listOfficeMlsId: string) => boolean;
}

const GlobalSiteHeaderContext = createContext<GlobalSiteHeaderContextType>({
  modalSection: 'menu',
  toSetModalSection: () => {},
  placesSearchObj: null,
  isListingFavorited: () => false,
  deleteSavedListing: () => {},
  saveListing: () => {},
});

interface GlobalSiteHeaderContextProviderProps {
  children: React.ReactNode;
}

export interface SavedPropertyData {
  savePropertyIds: Record<
    string,
    {
      CognitoId: string;
      FavoriteId: string;
      ListingId: string;
      ListOfficeMlsId: string;
      Timestamp: string;
    }
  >;
  userId: string;
}

interface CustomPolygonIndex {
  [key: string]: {
    polygon: {
      lat: number;
      lng: number;
    }[];
  };
}

export type CustomPolygonItem = CustomPolygonIndex & {
  countryCode: string;
};

export const useGlobalSiteHeaderContext = () => useContext(GlobalSiteHeaderContext);

export const GlobalSiteHeaderContextProvider: FC<GlobalSiteHeaderContextProviderProps> = ({
  children,
}) => {
  const api = useRef(new evAPI());
  const [modalSection, setModalSection] = useState('menu');
  const [placesSearchObj, setPlacesSearchObj] = useState<any | null>(null);
  const [savedProperties, setSavedProperties] = useState<UserFavorite[] | null>(null);
  const [isLoadingSavedProperties, setIsLoadingSavedProperties] = useState(true);

  const toSetModalSection = (section) => {
    setModalSection(section);
  };

  const getSearchObjForPlaces = async () => {
    let json;
    try {
      const url = 'https://evpublic.s3.us-west-2.amazonaws.com';
      const [searchJsonRes, searchPolygonRes] = await Promise.all([
        axios.get(`${url}/search.json?t=${Date.now()}`),
        axios.get(`${url}/polygonsearch.json?t=${Date.now()}`),
      ]);
      json = searchJsonRes.data.concat(searchPolygonRes.data ?? []);
    } catch (error) {
      console.log(error);
    }
    setPlacesSearchObj(json ?? []);
  };

  useEffect(() => {
    getUserSavedProperties();
    getSearchObjForPlaces();
  }, []);

  const getUserSavedProperties = useCallback(async (useCache: boolean = false) => {
    const data = await api.current.listFavoriteProperties('asc', useCache);
    setSavedProperties(data);
    setIsLoadingSavedProperties(false);
  }, []);

  const saveListing = useCallback(
    async (userId: string, listingId: string, listOfficeMlsId: string) => {
      // Save the listing
      await api.current.saveListing(userId, listingId, listOfficeMlsId);
      // Get the refreshed data from local storage.
      await getUserSavedProperties(true);
    },
    [],
  );

  const deleteSavedListing = useCallback(async (userId: string, listingId: string) => {
    // Delete the listing
    await api.current.deleteSavedListing(userId, listingId);
    // Get the refreshed data from local storage.
    await getUserSavedProperties(true);
  }, []);

  const isListingFavorited = useCallback(
    (listingId: string, listOfficeMlsId: string): boolean => {
      if (savedProperties == null && isLoadingSavedProperties) {
        getUserSavedProperties();
      }
      return (
        savedProperties?.some(
          (property) =>
            property.ListingId === listingId && property.ListOfficeMlsId === listOfficeMlsId,
        ) || false
      );
    },
    [savedProperties],
  );

  return (
    <GlobalSiteHeaderContext.Provider
      value={{
        modalSection,
        toSetModalSection,
        placesSearchObj,
        isListingFavorited,
        deleteSavedListing,
        saveListing,
      }}
    >
      {children}
    </GlobalSiteHeaderContext.Provider>
  );
};
